/**
 * A question the case agent asks a person, answered in words or by one of
 * the options offered (a key each, 1 … 9; a choice that takes a typed value
 * asks for it); the checklist item it's about is named. Enter records. A
 * question in words may instead be declined as not needed, saying why. A
 * file may go with the answer, or be the answer.
 */

import { useState } from 'react';

import { Button, Group, Radio, Stack, Text, Textarea, TextInput } from '@mantine/core';

import type { ChecklistItem, Intervention } from '@/types/workbench';
import { CHECKLIST_STATUS } from '../format';
import { useWorkbenchKeys } from '../useWorkbenchKeys';
import { AttachFile, type AttachedFile } from './AttachFile';
import { AskHeading, Kbd, useAnswer } from './common';
import { NotNeeded } from './NotNeeded';
import classes from '../NeedsYou.module.css';

interface QuestionPaneProps {
  reportId: string;
  ask: Intervention;
  subjectName: string | null;
  /** The checklist item it's about, where its target names one. */
  item: ChecklistItem | null;
  onAnswered: (ids: string[]) => void | Promise<void>;
  keyboard: boolean;
}

export function QuestionPane({
  reportId,
  ask,
  subjectName,
  item,
  onAnswered,
  keyboard,
}: Readonly<QuestionPaneProps>) {
  const { submitting, submit } = useAnswer(reportId, onAnswered);
  const [choice, setChoice] = useState<string | null>(null);
  const [value, setValue] = useState('');
  const [text, setText] = useState('');
  const [note, setNote] = useState('');
  const [attached, setAttached] = useState<AttachedFile | null>(null);
  const options = ask.options;
  const typed = options.find((o) => o.id === choice)?.takesText ?? false;
  const ready =
    options.length > 0
      ? choice !== null && (!typed || value.trim() !== '')
      : text.trim() !== '' || attached !== null;

  const record = () => {
    if (!ready || submitting) {
      return;
    }
    void submit(ask, {
      ...(options.length > 0
        ? { choice: choice ?? undefined }
        : text.trim()
          ? { text: text.trim() }
          : {}),
      ...(typed ? { value: value.trim() } : {}),
      ...(attached ? { conversation_attachment_id: attached.id } : {}),
      ...(note.trim() ? { note: note.trim() } : {}),
    });
  };
  const keys: Record<string, () => void> = { Enter: record, 'Mod+Enter': record };
  options.forEach((option, at) => {
    if (at < 9) {
      keys[String(at + 1)] = () => setChoice(option.id);
    }
  });
  useWorkbenchKeys(keys, keyboard);

  const chosen = options.find((o) => o.id === choice);
  return (
    <>
      <div className={classes.detailBody} data-testid="pane-question">
        <Stack gap="sm">
          <AskHeading
            section={ask.section}
            subjectName={subjectName}
            question={ask.question}
            why={ask.reason}
            blocking={ask.waitingTaskId !== null}
          />
          {item && (
            <Text size="xs" c="dimmed" data-testid="question-item">
              About the check: {item.key} {item.title} · {CHECKLIST_STATUS[item.status].label}
              {item.note ? ` — ${item.note}` : ''}
            </Text>
          )}
        </Stack>
      </div>
      <Stack gap={6} className={classes.answers}>
        {options.length > 0 ? (
          <Radio.Group value={choice} onChange={setChoice}>
            <Group gap="md">
              {options.map((option, at) => (
                <Radio
                  key={option.id}
                  size="xs"
                  value={option.id}
                  label={at < 9 ? `${option.label} (${at + 1})` : option.label}
                  data-testid={`question-option-${option.id}`}
                />
              ))}
            </Group>
          </Radio.Group>
        ) : (
          <Textarea
            size="xs"
            autosize
            minRows={2}
            placeholder="Your answer (⌘↵ records)"
            aria-label="Your answer"
            value={text}
            onChange={(e) => setText(e.currentTarget.value)}
            data-testid="question-text"
          />
        )}
        {typed && (
          <TextInput
            size="xs"
            placeholder="Type it"
            value={value}
            onChange={(e) => setValue(e.currentTarget.value)}
            data-testid="question-typed"
          />
        )}
        <Group gap={6} wrap="nowrap">
          <TextInput
            size="xs"
            placeholder="A note (optional)"
            aria-label="Note"
            value={note}
            onChange={(e) => setNote(e.currentTarget.value)}
            style={{ flex: 1 }}
          />
          <AttachFile reportId={reportId} attached={attached} onChange={setAttached} />
          <Button
            size="xs"
            onClick={record}
            disabled={!ready}
            loading={submitting}
            data-testid="question-record"
          >
            {chosen ? `Record “${chosen.label}”` : 'Record'}
            <Kbd>Enter</Kbd>
          </Button>
        </Group>
        {options.length === 0 && (
          <NotNeeded onDecline={(why) => submit(ask, { not_needed: true, note: why })} />
        )}
      </Stack>
    </>
  );
}
